/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';
import {
  buildEvalByRubricIdPath,
  clearGraphModelError,
  getErrorMessage,
  handleGraphServiceHttpError,
  setNotification,
  unwrapServiceData
} from './graphServiceUtils';

/** PUT /graphs/:graphId/eval/:rubricId/rubrictext */
export default class SaveEvaluationRubricTextService extends BaseService {
  constructor() {
    super(constants.SAVE_EVALUATION_RUBRIC_TEXT, 'saveEvaluationRubricTextStatus');
  }

  buildSavedRubricFromResponse(data) {
    const body = data?.data != null && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;
    return {
      evalRubric: body?.evalRubric ?? body?.rubricText ?? '',
      rubricName: body?.rubricName ?? '',
      ...(body?.rubricId != null && body?.rubricId !== '' ? { rubricId: String(body.rubricId) } : {})
    };
  }

  publishSaveError(data, msg) {
    setNotification({
      type: 'error',
      message: msg
    });
    Framework.getStoreRegistry().getStore('graphModel').setError(msg);
    Framework.getEventManager().publish(constants.SAVE_EVALUATION_RUBRIC_TEXT_ERROR, { data, message: msg });
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, rubricId, requestBody } = payload;
    const url = buildEvalByRubricIdPath(graphId, rubricId, 'rubrictext');

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').put(url, requestBody, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').put(url, requestBody, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      clearGraphModelError();
      setNotification(
        data?.message
          ? { type: 'success', message: data.message }
          : { type: 'success', langMessage: 'graphEvaluation.SAVE_RUBRIC_TEXT_SUCCESS' }
      );
      const savedRubric = this.buildSavedRubricFromResponse(unwrapServiceData(response) ?? data);
      Framework.getEventManager().publish(constants.SAVE_EVALUATION_RUBRIC_TEXT_SUCCESS, {
        data,
        ...savedRubric
      });
      return;
    }

    const msg = getErrorMessage(data, 'Unknown error');
    this.publishSaveError(data, msg);
  }

  handleError(error) {
    handleGraphServiceHttpError(error, {
      fallbackLangMessage: 'graphEvaluation.SAVE_RUBRIC_TEXT_RETRY',
      errorEvent: constants.SAVE_EVALUATION_RUBRIC_TEXT_ERROR,
      updateGraphModelError: true
    });
  }
}
